/**
 * Живой стакан Polymarket в ту же вилку, что карточка и калькулятор.
 *
 * CLOB уже крутит лестницу. Суммы, кэф и ROI оставались кадром ленты — на
 * сайте ордер ехал, у нас 33.79 $ стояли. Сюда кладём asks в ногу и
 * пересчитываем `buildRow`: второе плечо и плюс едут сами.
 */

import { useSyncExternalStore } from 'react';

import { PRIVATE_MIRROR, RU_FEED } from '../lib/feedContext';
import { cupisBookKey } from './cupisPoly';
import { Decimal } from 'decimal.js';

import type { BookLevel } from '../lib/calc';
import type { Fork, ForkLeg } from '../lib/protocol';
import { isShareLeg } from '../lib/shareVenue';
import { isPolymarket } from './order';

const PRICE_STEP = 3;

function bookKey(leg: { url?: string | null; betUrl?: string | null; asset?: string | null }): string | null {
  if (PRIVATE_MIRROR || RU_FEED) return cupisBookKey({ ...leg, url: leg.betUrl ?? leg.url });
  if (leg.asset) return `a:${leg.asset}`;
  if (leg.url) return `u:${leg.url}`;
  return null;
}

function sameAsks(a: readonly BookLevel[], b: readonly BookLevel[]): boolean {
  if (a.length !== b.length) return false;
  return a.every((row, i) => row[0] === b[i]![0] && row[1] === b[i]![1]);
}

function polyShareLeg(fork: Fork): ForkLeg | undefined {
  return (fork.legs ?? []).find((leg) => isPolymarket(leg.s) && isShareLeg(leg));
}

const books = new Map<string, { asks: BookLevel[]; at: number }>();
let tick = 0;
const listeners = new Set<() => void>();

function emit(): void {
  tick += 1;
  for (const listen of listeners) listen();
}

function subscribe(listen: () => void): () => void {
  listeners.add(listen);
  return () => {
    listeners.delete(listen);
  };
}

/** Подписка: пришёл новый стакан. */
export function useLiveBookTick(): number {
  return useSyncExternalStore(subscribe, () => tick, () => 0);
}

export function rememberLiveAsks(
  leg: { url?: string | null; betUrl?: string | null; asset?: string | null },
  asks: readonly BookLevel[],
  at = Date.now(),
): void {
  const key = bookKey(leg);
  if (key === null || asks.length === 0) return;
  const copy = asks.map(([price, size]) => [String(price), String(size)] as BookLevel);
  const prev = books.get(key);
  if (prev !== undefined && sameAsks(prev.asks, copy)) {
    books.set(key, { asks: prev.asks, at });
    return;
  }
  books.set(key, { asks: copy, at });
  emit();
}

export function liveAsksOf(leg: { url?: string | null; betUrl?: string | null; asset?: string | null }): BookLevel[] | null {
  const key = bookKey(leg);
  if (key === null) return null;
  const stored = books.get(key);
  if ((PRIVATE_MIRROR || RU_FEED) && stored && Date.now() - stored.at > 10000) return null;
  return stored?.asks ?? null;
}

/** Тесты: вычистить живые стаканы. */
export function resetLiveAsks(): void {
  if (books.size === 0) return;
  books.clear();
  emit();
}

/**
 * Подставить живые asks в биржевую ногу. Кэф и цена шара — с лучшего уровня:
 * объём по-прежнему идёт по всей книге. Замок кэфа — не трогаем.
 */
/**
 * Ключ удержания обещания: кадр ленты и замки человека, не живой CLOB.
 * Иначе каждый тик стакана пересобирает купон и драйвер перезагружает Polymarket.
 */
export function liveHoldKey(
  fork: Fork,
  extra = '',
): string {
  const tape = (fork.legs ?? []).map((leg) => `${leg.s}:${leg.odds}:${leg.share ?? ''}`).join('|');
  return `${tape}|${extra}`;
}

export function paintLiveAsks(
  fork: Fork,
  asks?: readonly BookLevel[] | null,
  locked: ReadonlySet<string> = new Set(),
): Fork {
  const leg = polyShareLeg(fork);
  if (leg === undefined || locked.has(leg.s)) return fork;
  const book = asks ?? liveAsksOf(leg);
  if (book === null || book.length === 0) return fork;
  if (leg.book !== undefined && sameAsks(leg.book, book)) return fork;
  const best = new Decimal(book[0]![0]);
  if (!best.gt(0) || !best.lt(1)) return fork;
  const share = best.toDecimalPlaces(PRICE_STEP, Decimal.ROUND_UP).toString();
  const odds = new Decimal(1).div(best).toDecimalPlaces(3, Decimal.ROUND_DOWN).toFixed(3);
  const next: ForkLeg = { ...leg, book: book.map(([price, size]) => [price, size]), share, odds };
  const legs = (fork.legs ?? []).map((one) => (one === leg ? next : one));
  return { ...fork, legs, o: legs.map((one) => one.odds) };
}
